import React, { useState, useEffect } from 'react';
import './DashboardAnalytics.css';

export default function DashboardAnalytics({ userRole, subscription, token, qrHistory = [] }) {
  const [stats, setStats] = useState({
    totalQRs: 0,
    activeQRs: 0,
    expiredQRs: 0,
    qrsByType: {},
    qrsByMonth: {},
    avgScansPerQR: 0,
    topPerformingQRs: []
  });

  useEffect(() => {
    calculateStats();
  }, [qrHistory]);

  const calculateStats = () => {
    // Garantir que qrHistory seja sempre um array
    const safeQrHistory = Array.isArray(qrHistory) ? qrHistory : [];
    
    if (safeQrHistory.length === 0) {
      return;
    }

    const total = safeQrHistory.length;
    const active = safeQrHistory.filter(qr => qr.status === 'active').length;
    const expired = safeQrHistory.filter(qr => qr.status === 'expired').length;

    // QRs por tipo
    const byType = {};
    safeQrHistory.forEach(qr => {
      const type = qr.config?.type || 'URL';
      byType[type] = (byType[type] || 0) + 1;
    });

    // QRs por mês (últimos 6 meses)
    const byMonth = {};
    safeQrHistory.forEach(qr => {
      const date = new Date(qr.createdAt);
      const monthKey = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
      byMonth[monthKey] = (byMonth[monthKey] || 0) + 1;
    });

    setStats({
      totalQRs: total,
      activeQRs: active,
      expiredQRs: expired,
      qrsByType: byType,
      qrsByMonth: byMonth,
      avgScansPerQR: 0,
      topPerformingQRs: []
    });
  };

  const getTypeColor = (index) => {
    const colors = ['#3b82f6', '#8b5cf6', '#ec4899', '#f59e0b', '#10b981', '#ef4444'];
    return colors[index % colors.length];
  };

  const getMonthLabel = (monthKey) => {
    const [year, month] = monthKey.split('-');
    const months = ['Jan', 'Fev', 'Mar', 'Abr', 'Mai', 'Jun', 'Jul', 'Ago', 'Set', 'Out', 'Nov', 'Dez'];
    return `${months[parseInt(month) - 1]}/${year}`;
  };

  return (
    <div className="analytics-container">
      {/* Cards de Métricas Principais */}
      <div className="analytics-metrics-grid">
        <div className="metric-card metric-primary">
          <div className="metric-icon">📊</div>
          <div className="metric-content">
            <h3>Total de QR Codes</h3>
            <p className="metric-value">{stats.totalQRs}</p>
            <span className="metric-label">Criados até agora</span>
          </div>
        </div>

        <div className="metric-card metric-success">
          <div className="metric-icon">✅</div>
          <div className="metric-content">
            <h3>QR Codes Ativos</h3>
            <p className="metric-value">{stats.activeQRs}</p>
            <span className="metric-label">Em uso atualmente</span>
          </div>
        </div>

        <div className="metric-card metric-warning">
          <div className="metric-icon">⏱️</div>
          <div className="metric-content">
            <h3>QR Codes Expirados</h3>
            <p className="metric-value">{stats.expiredQRs}</p>
            <span className="metric-label">Fora do período</span>
          </div>
        </div>

        <div className="metric-card metric-info">
          <div className="metric-icon">📈</div>
          <div className="metric-content">
            <h3>Taxa de Uso</h3>
            <p className="metric-value">
              {stats.totalQRs > 0 ? Math.round((stats.activeQRs / stats.totalQRs) * 100) : 0}%
            </p>
            <span className="metric-label">QR Codes ativos</span>
          </div>
        </div>
      </div>

      {/* Gráficos */}
      <div className="analytics-charts-grid">
        {/* QR Codes por Tipo */}
        <div className="chart-card">
          <div className="chart-header">
            <h3>📋 Distribuição por Tipo</h3>
            <p>Categorias mais utilizadas</p>
          </div>
          <div className="chart-content">
            {Object.keys(stats.qrsByType).length > 0 ? (
              <div className="bar-chart">
                {Object.entries(stats.qrsByType)
                  .sort((a, b) => b[1] - a[1])
                  .map(([type, count], index) => (
                    <div key={type} className="bar-item">
                      <div className="bar-label">
                        <span>{type}</span>
                        <span className="bar-value">{count}</span>
                      </div>
                      <div className="bar-wrapper">
                        <div 
                          className="bar-fill" 
                          style={{ 
                            width: `${(count / stats.totalQRs) * 100}%`,
                            backgroundColor: getTypeColor(index)
                          }}
                        />
                      </div>
                    </div>
                  ))}
              </div>
            ) : (
              <div className="chart-empty">
                <p>📊 Nenhum dado disponível ainda</p>
              </div>
            )}
          </div>
        </div>

        {/* QR Codes por Mês */}
        <div className="chart-card">
          <div className="chart-header">
            <h3>📅 Criação ao Longo do Tempo</h3>
            <p>Últimos meses</p>
          </div>
          <div className="chart-content">
            {Object.keys(stats.qrsByMonth).length > 0 ? (
              <div className="timeline-chart">
                {Object.entries(stats.qrsByMonth)
                  .sort((a, b) => a[0].localeCompare(b[0]))
                  .slice(-6)
                  .map(([month, count]) => {
                    const maxCount = Math.max(...Object.values(stats.qrsByMonth));
                    const height = (count / maxCount) * 100;
                    return (
                      <div key={month} className="timeline-item">
                        <div className="timeline-bar-wrapper">
                          <div 
                            className="timeline-bar" 
                            style={{ height: `${height}%` }}
                            title={`${count} QR Codes`}
                          >
                            <span className="timeline-value">{count}</span>
                          </div>
                        </div>
                        <span className="timeline-label">{getMonthLabel(month)}</span>
                      </div>
                    );
                  })}
              </div>
            ) : (
              <div className="chart-empty">
                <p>📅 Nenhum dado disponível ainda</p>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Estatísticas Adicionais */}
      <div className="analytics-stats-grid">
        <div className="stat-card">
          <div className="stat-icon">🎯</div>
          <div className="stat-content">
            <h4>Tipo Mais Usado</h4>
            <p className="stat-value">
              {Object.keys(stats.qrsByType).length > 0
                ? Object.entries(stats.qrsByType).sort((a, b) => b[1] - a[1])[0][0]
                : 'N/A'}
            </p>
          </div>
        </div>

        <div className="stat-card">
          <div className="stat-icon">📆</div>
          <div className="stat-content">
            <h4>Mês Mais Produtivo</h4>
            <p className="stat-value">
              {Object.keys(stats.qrsByMonth).length > 0
                ? getMonthLabel(Object.entries(stats.qrsByMonth).sort((a, b) => b[1] - a[1])[0][0])
                : 'N/A'}
            </p>
          </div>
        </div>

        <div className="stat-card">
          <div className="stat-icon">⚡</div>
          <div className="stat-content">
            <h4>Média Mensal</h4>
            <p className="stat-value">
              {Object.keys(stats.qrsByMonth).length > 0
                ? Math.round(Object.values(stats.qrsByMonth).reduce((a, b) => a + b, 0) / Object.keys(stats.qrsByMonth).length)
                : 0}
            </p>
          </div>
        </div>

        <div className="stat-card">
          <div className="stat-icon">💎</div>
          <div className="stat-content">
            <h4>Plano Atual</h4>
            <p className="stat-value">
              {subscription === 'pro' && 'PRO'}
              {subscription === 'business' && 'BUSINESS'}
              {userRole === 'admin' && 'ADMIN'}
              {!subscription && subscription !== 'pro' && subscription !== 'business' && 'FREE'}
            </p>
          </div>
        </div>
      </div>

      {/* Mensagem de Incentivo */}
      {stats.totalQRs === 0 && (
        <div className="analytics-empty-state">
          <div className="empty-icon">🎨</div>
          <h3>Comece a Criar seus QR Codes!</h3>
          <p>Suas estatísticas aparecerão aqui assim que você criar seus primeiros QR Codes.</p>
          <a href="/qrcode-generator" className="empty-cta">
            Criar Meu Primeiro QR Code
          </a>
        </div>
      )}
    </div>
  );
}
